import React from "react";
import { useTranslation } from "react-i18next";
import { PurchaseDocDetail, PurchaseDocValidationError } from "types/InvoiceInbox";

/**
 * What the document reader saw for a field it could not confirm, shown under
 * that field in the panel's cards, with a one-click accept where there is a
 * clean value to write back.
 */

/**
 * The validator report is keyed by code, not by column, so finding "is there
 * something to offer for THIS field" means scanning it. A `suggestion`/`raw`
 * is always for `fields[0]` (see the type) — a field only ever named as
 * context in `fields[1..]` has nothing of its own to accept.
 * Kept as a small pure function so it can be unit-tested without rendering.
 */
export function findFieldSuggestion(
  validationErrors: PurchaseDocValidationError[] | null | undefined,
  column: string
): PurchaseDocValidationError | undefined {
  if (!validationErrors) return undefined;
  // One field can be named by several checks (grounding and arithmetic); the
  // one that carries something to show wins over one that does not.
  const named = validationErrors.filter((entry) => entry.fields && entry.fields[0] === column);
  return (
    named.find((entry) => entry.suggestion !== undefined) ||
    named.find((entry) => entry.raw !== undefined) ||
    named[0]
  );
}

// Why the value shown next to a field could not be trusted as-is, by
// validator code. `not_grounded` and the arithmetic checks are the two
// current families; anything else (including future codes) falls back to a
// generic "not confirmed" rather than showing nothing.
const SUGGESTION_REASON_ARITHMETIC = {
  key: "invoice_inbox_suggestion_reason_arithmetic",
  defaultValue: "does not add up with the other amounts",
};
const SUGGESTION_REASON_BY_CODE: Record<string, { key: string; defaultValue: string }> = {
  not_grounded: {
    key: "invoice_inbox_suggestion_reason_not_grounded",
    defaultValue: "not found in the document's text (may be part of an image)",
  },
  amount_due: SUGGESTION_REASON_ARITHMETIC,
  net_plus_vat: SUGGESTION_REASON_ARITHMETIC,
  vat_taxable_sum: SUGGESTION_REASON_ARITHMETIC,
  vat_tax_sum: SUGGESTION_REASON_ARITHMETIC,
  amount_unparsed: {
    key: "invoice_inbox_suggestion_reason_amount_unparsed",
    defaultValue: "number format unclear",
  },
};
const SUGGESTION_REASON_DEFAULT = { key: "invoice_inbox_suggestion_reason_default", defaultValue: "not confirmed" };

function suggestionReasonFor(code: string): { key: string; defaultValue: string } {
  return SUGGESTION_REASON_BY_CODE[code] || SUGGESTION_REASON_DEFAULT;
}

/** A field the backend could not verify gets an amber edge, so the eye finds it. */
export function reviewMarkClass(doc: PurchaseDocDetail["doc"], column: string): string {
  return (doc.needsReviewFields || []).indexOf(column) !== -1 ? "border-l-4 border-amber-400 pl-2" : "";
}

/** What every card needs to offer a suggestion: whether it may be accepted
 * right now, and how. */
export type SuggestionAccess = {
  canAccept: boolean;
  busy: boolean;
  onAccept: (column: string, suggestion: string) => void;
};

type Props = {
  doc: PurchaseDocDetail["doc"];
  column: string;
  value: unknown;
  access: SuggestionAccess;
};

// Renders nothing when the field already has a value (nothing to offer over
// what is already there) or no validator entry names it.
export function FieldSuggestion({ doc, column, value, access }: Props) {
  const { t } = useTranslation();
  if (value !== null && value !== undefined && value !== "") return null;
  const entry = findFieldSuggestion(doc.validationErrors, column);
  if (!entry) return null;
  const shown = entry.suggestion ?? entry.raw;
  if (shown === undefined) return null;
  const reason = suggestionReasonFor(entry.code);
  return (
    <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
      <div>
        {t("invoice_inbox_suggestion_label", { defaultValue: "Read from the document" })}:{" "}
        <span className="font-semibold">{shown}</span>
      </div>
      <div className="text-amber-700">{t(reason.key, { defaultValue: reason.defaultValue })}</div>
      {/* amount_unparsed carries `raw`, never `suggestion` — text only,
          because the format is ambiguous enough that there is no single
          clean value to write back. */}
      {entry.suggestion && access.canAccept && (
        <button
          type="button"
          className="mt-1 rounded border border-amber-400 bg-white px-2 py-0.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          disabled={access.busy}
          onClick={() => access.onAccept(column, entry.suggestion as string)}
        >
          {t("invoice_inbox_suggestion_accept", { defaultValue: "Accept" })}
        </button>
      )}
    </div>
  );
}
